import { useCallback, useState } from 'react'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Delete, Download } from '@factorialco/f0-react/icons/app'
import {
  DeletePayrollCycleFileDocument,
  DeletePayrollCycleFileMutation,
  DownloadPayrollCycleFileDocument,
  DownloadPayrollCycleFileMutation,
  PayrollRunFileCategoryFragment,
  PayrollRunFileFragment,
  PayrollRunFilesDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import buildUrl from 'modules/core/lib/buildUrl'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'
import { download } from 'modules/core/lib/download'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import DeleteFileModal from 'modules/bookkeepers/components/PayrollCycle/DeleteFileModal'

interface FilesDataCollectionProps {
  companyId: number
  payrollRunId: number
  categories: Array<{ id: number } & PayrollRunFileCategoryFragment>
  emptyState: React.ComponentProps<typeof OneDataCollection>['emptyStates']
  refetchTrigger?: number
  sidebarView?: boolean
}

const FilesCategoryList = ({
  companyId,
  payrollRunId,
  categories,
  emptyState,
  refetchTrigger,
  sidebarView = false,
}: FilesDataCollectionProps) => {
  const { access } = useCurrentSession()
  const [files, setFiles] = useState<PayrollRunFileFragment[]>([])
  const [fileToDelete, setFileToDelete] = useState<PayrollRunFileFragment | null>(null)

  const getCategoryCount = useCallback(
    (fileCategoryId: number) => {
      return files.filter((file) => file.fileCategoryId === fileCategoryId).length
    },
    [files]
  )

  const [deleteFile, { loading: deleting }] = useMutation(DeletePayrollCycleFileDocument, {
    extractorFn: (data: DeletePayrollCycleFileMutation) =>
      data.compensationsMutations.deleteCompensationsFile,
  })

  const [downloadFile] = useMutation(DownloadPayrollCycleFileDocument, {
    extractorFn: (data: DownloadPayrollCycleFileMutation) =>
      data.compensationsMutations.downloadCompensationsFile,
  })

  const handleDownload = useCallback(
    async (file: PayrollRunFileFragment) => {
      const response = await downloadFile({ variables: { id: file.id } })

      const result = handleResult(response, () => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('bookkeepers.clients.payroll_cycles.documents.errors.download'),
        })
      })

      if (result.success && result.data.file?.downloadUrl) {
        download(result.data.file.downloadUrl, file.fileName)
      }
    },
    [downloadFile]
  )

  const handleDelete = useCallback(
    async (file: PayrollRunFileFragment) => {
      const response = await deleteFile({
        variables: { fileId: file.id },
        refetchQueries: [
          {
            query: PayrollRunFilesDocument,
            variables: {
              companyId,
              payrollRunId,
              fileCategoryIds: categories.map((category) => category.id),
            },
          },
        ],
      })

      const result = handleResult(response, () => {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('bookkeepers.clients.payroll_cycles.documents.errors.delete'),
        })
      })

      if (result.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.deleted_successfully'),
        })

        setFileToDelete(null)
      }
    },
    [deleteFile, companyId, payrollRunId, categories]
  )

  const sourceFiles = useGraphqlDataCollectionSource(
    {
      document: PayrollRunFilesDocument,
      mapData: (data) => {
        const nodes = data.compensations.filesConnection.nodes
        setFiles(nodes)
        return nodes
      },
      mapPagination: (total) => ({
        total: total.compensations.filesConnection.totalCount ?? 0,
      }),
      paginationType: 'infinite-scroll',
      mapVariables: () => {
        return {
          companyId,
          payrollRunId,
          fileCategoryIds: categories.map((category) => category.id),
        }
      },
      defaultSortings: {
        field: 'createdAt',
        order: 'desc',
      },
      filters: {},
      presets: [],
      itemActions: (file: PayrollRunFileFragment) => [
        {
          label: i18n.t('common.download'),
          icon: Download,
          onClick: () => handleDownload(file),
          type: 'primary',
        },
        {
          label: i18n.t('common.delete'),
          icon: Delete,
          onClick: () => setFileToDelete(file),
          critical: true,
          enabled: file.status === 'pending' && file.createdBy?.id === access.id,
        },
      ],
      grouping: {
        collapsible: true,
        mandatory: true,
        hideSelector: true,
        defaultOpenGroups: true,
        groupBy: {
          fileCategoryId: {
            name: 'Category',
            label(fileCategoryId) {
              const categoryKey = categories.find(
                (category) => category.id === Number(fileCategoryId)
              )?.categoryType

              switch (categoryKey) {
                case 'payroll_result':
                  return i18n.t(
                    'bookkeepers.clients.payroll_cycles.documents.file_categories.payroll_result'
                  )
                case 'payslips':
                  return i18n.t(
                    'bookkeepers.clients.payroll_cycles.documents.file_categories.payslips'
                  )
                case 'payment_method':
                  return i18n.t(
                    'bookkeepers.clients.payroll_cycles.documents.file_categories.payment_method'
                  )
                case 'others':
                  return i18n.t(
                    'bookkeepers.clients.payroll_cycles.documents.file_categories.others'
                  )
                case 'exported':
                  return i18n.t(
                    'bookkeepers.clients.payroll_cycles.documents.file_categories.exported'
                  )
                default:
                  return ''
              }
            },
            itemCount: (fileCategoryId) => getCategoryCount(Number(fileCategoryId)),
          },
        },
      },
    },
    [refetchTrigger]
  )

  return (
    <>
      <OneDataCollection
        storage={false}
        source={sourceFiles}
        emptyStates={emptyState}
        visualizations={[
          {
            type: 'list',
            options: {
              itemDefinition: (item: PayrollRunFileFragment) => {
                const creationDate = i18nDateToString(
                  stringToDate(item.createdAt, 'backend_datetime'),
                  'ui_short_with_time'
                )
                return {
                  title: item.fileName,
                  description: sidebarView
                    ? [`${item.createdBy?.firstName} ${item.createdBy?.lastName}`, creationDate]
                    : undefined,
                  avatar: {
                    type: 'file',
                    file: {
                      name: item.fileName,
                      type: '',
                    },
                  },
                }
              },
              fields: sidebarView
                ? []
                : [
                    {
                      label: i18n.t('bookkeepers.clients.payroll_cycles.documents.list.created_at'),
                      render: (item: PayrollRunFileFragment) => ({
                        type: 'text',
                        value: i18nDateToString(
                          stringToDate(item.createdAt, 'backend_datetime'),
                          'ui_short_with_time'
                        ),
                      }),
                    },
                    {
                      label: i18n.t('bookkeepers.clients.payroll_cycles.documents.list.created_by'),
                      render: (item: PayrollRunFileFragment) => ({
                        type: 'person',
                        value: {
                          firstName: item.createdBy?.firstName ?? '',
                          lastName: item.createdBy?.lastName ?? '',
                          src: buildUrl(item.createdBy?.avatar ?? ''),
                        },
                      }),
                    },
                  ],
            },
          },
        ]}
      />
      {fileToDelete && (
        <DeleteFileModal
          onClose={() => setFileToDelete(null)}
          onSubmit={() => handleDelete(fileToDelete)}
          deleting={deleting}
        />
      )}
    </>
  )
}

export default FilesCategoryList
